// contexts/AuthContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userHash, setUserHash] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Ensure hash exists in localStorage
        let userHash = localStorage.getItem('qxai_user_hash');

        if (!userHash) {
          // Regenerate hash if missing
          userHash = await hashEmail(user.email);
          localStorage.setItem('qxai_user_hash', userHash);
        }

        setUserHash(userHash);
        setCurrentUser(user);
      } else {
        // User signed out - clear hash
        localStorage.removeItem('qxai_user_hash');
        setCurrentUser(null);
        setUserHash(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const hashEmail = async (email) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('qxai_user_hash');
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value = {
    currentUser,
    user: currentUser,
    userHash,
    loading,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
